import { all, call, delay, put, select, takeEvery, takeLatest } from 'redux-saga/effects';

import {
  createPostFormSelector,
  postsActions,
  postSelector,
  postsScheduledSelector,
  postsSelector,
} from './index';
import { toastActions } from '../toast';
import { Api } from '$api';
import {
  DeletePostAction,
  HideAuthorAction,
  LoadEditDataAction,
  LoadMoreCommentsAction,
  LoadPostAction,
  LoadRepliesAction,
  LoadScheduledAction,
  PinPostAction,
  PublishAction,
  PublishCommentAction,
  PublishScheduledAction,
  ReportCommentAction,
  ReportPostAction,
  SavePostAction,
  SendAdConversionAction,
  ShareAction,
  ToggleLikeAction,
} from './interface';
import { RootState } from '../rootReducer';
import { attachActions, attachItemsSelector, attachSelector } from '../attach';
import { currentUserSelector, mainActions } from '../main';
import { hideModal, showModal } from '$navigation/helper';
import { authSelector } from '$store/auth';
import { batchActions } from '$store';
import { _confirm, confirmSheet, statReachGoal } from '$utils';
import { removePostDraft } from '$utils/postDrafts';
import { groupsActions } from '$store/groups';
import { profileActions } from '$store/profile';
import { t } from '$translation/helper';
import { feedActions } from '$store/feed';
import { PostType, ViewPost } from '$store/models';
import { rouletteActions } from '$store/roulette';

function* publishWorker(action: PublishAction) {
  try {
    const ownerId = action.payload;
    const form = yield select((state: RootState) =>
      createPostFormSelector(state, `post_${ownerId}`),
    );
    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, `post_${ownerId}`),
    );
    const { links, timers } = yield select(attachSelector);

    if (attachments === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (form.text.length === 0 && !attachments.length) {
      return yield put(toastActions.setToastFail(t('im_empty_error')));
    }

    // let hasMedia = false;
    // for (let item of attachments) {
    //   if (item.type === 'photo' || item.type === 'video') {
    //     hasMedia = true;
    //     break;
    //   }
    // }

    // if (!hasMedia) {
    //   return yield put(toastActions.setToastFail(t('attach_media_empty_error')));
    // }

    let text = form.text;
    const link = links[`post_${ownerId}`];
    const timer = timers[`post_${ownerId}`];
    if (link && link.object && link.object.originalLink === text.trim()) {
      text = '';
    }

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/posts/new', {
      ownerId,
      text,
      type: form.type,
      parentId: 0,
      attachments,
      timer: timer > 0 ? Math.floor(timer / 1000) : 0,
      skipBotPost: !form.sendToTelegram,
    });
    yield put(toastActions.setToastSuccess(t('success')));
    yield put(
      postsActions.updateFormText({
        formId: `post_${ownerId}`,
        text: '',
      }),
    );
    const currentUser = yield select(currentUserSelector);
    if (currentUser?.id) {
      yield call(removePostDraft, currentUser.id, `post_${ownerId}`);
    }
    yield put(rouletteActions.clear());
    yield put(
      postsActions.setPost({
        id: data.post.id,
        updated: data.post,
      }),
    );
    if (timer > 0) {
      if (ownerId < 0) {
        yield put(groupsActions.incrementScheduledCount({ groupId: -ownerId }));
      } else {
        yield put(profileActions.incrementScheduledCount({ userId: ownerId }));
      }
    } else {
      yield put(
        batchActions(
          postsActions.unshiftToList({
            listId: String(ownerId),
            items: [data.post.id],
          }),
          postsActions.updateOrderList(String(ownerId)),
        ),
      );
    }
    yield put(attachActions.clear(`post_${ownerId}`));
    yield call(statReachGoal, 'wall_post');

    if (data.post.createdAt * 1000 - Date.now() < 10 * 1000) {
      // yield put(
      //   feedActions.unshiftFeed({
      //     item: {
      //       id: data.post.id,
      //       object: data.post.id,
      //       isSuggestion: false,
      //       type: FeedItemType.POST,
      //     },
      //     section
      //   }),
      // );
    }

    yield put(profileActions.incrementPostsCount(ownerId));
    yield put(postsActions.incTotalFields({ [form.type]: 1 }));

    yield call(hideModal);
    yield call(confirmSheet, t('post_publish_moderation'));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* toggleLikeWorker(action: ToggleLikeAction) {
  const postId = action.payload;
  const post = yield select((state: RootState) => postSelector(state, postId));

  try {
    const isAddLike = !post.isLiked;

    yield put(
      postsActions.setPost({
        id: postId,
        updated: {
          isLiked: isAddLike,
          likes: isAddLike ? post.likes + 1 : Math.max(0, post.likes - 1),
        },
      }),
    );

    const data = yield call(
      Api.post,
      `/likes/${postId}/post/${isAddLike ? 'add' : 'del'}`,
    );
    yield put(
      postsActions.setPost({
        id: postId,
        updated: {
          likes: +data.likes_count,
        },
      }),
    );

    if (isAddLike) {
      yield call(statReachGoal, 'like');
    }

    yield put(
      isAddLike
        ? profileActions.incrementTotalLikesCount(post.ownerId)
        : profileActions.decrementTotalLikesCount(post.ownerId),
    );
  } catch (e) {
    if (e.message === 'already_liked') {
      return;
    }

    yield put(toastActions.setToastFail(e.message));
    yield put(
      postsActions.setPost({
        id: postId,
        updated: {
          isLiked: post.isLiked,
          likes: post.likes,
        },
      }),
    );
  }
}

function* loadPostWorker(action: LoadPostAction) {
  try {
    const postId = action.payload;
    const post = yield call(Api.get, `/posts/${postId}`);
    const { parentId } = post.post;
    const posts: any[] = [post.post, ...post.comments];
    if (parentId && !(yield select(postsSelector))[parentId]) {
      posts.push((yield call(Api.get, `/posts/${parentId}`)).post);
    }

    yield put(
      batchActions(
        postsActions.setUnavailable({
          id: postId,
          isUnavailable: post.isUnavailable,
        }),
        mainActions.setUsers(post.users),
        mainActions.setGroups(post.groups),
        postsActions.setPosts(posts),
        postsActions.setList({
          listId: `comments_${postId}`,
          items: post.comments.map((comment) => comment.id),
        }),
        postsActions.setPostLoading({
          postId,
          isLoading: false,
        }),
        postsActions.setCommentsNextFrom({
          postId,
          nextFrom: post.commentsNextFrom,
        }),
      ),
    );
  } catch (e) {
    if (e.message === 'post not found') {
      yield put(postsActions.setIsDeletedPost(action.payload));
    } else {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

function* publishCommentWorker(action: PublishCommentAction) {
  try {
    const originalParent = action.payload;
    let parentId = action.payload;
    const form = yield select((state: RootState) =>
      createPostFormSelector(state, `comment_${originalParent}`),
    );
    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, `comment_${originalParent}`),
    );
    const replyTo = form.replyTo?.id ?? 0;
    const post = yield select((state: RootState) => postSelector(state, originalParent));

    if (attachments === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (form.text.length === 0 && !attachments.length) {
      return yield put(toastActions.setToastFail(t('im_empty_error')));
    }

    if (form.replyTo) {
      if (form.replyTo.level === 1) {
        parentId = form.replyTo.id;
      } else {
        parentId = form.replyTo.parentId;
      }
    }

    let groupId = 0;
    if (form.sender === 'group' && post && post.ownerId < 0) {
      groupId = Math.abs(post.ownerId);
    }

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/posts/new', {
      parentId,
      replyTo,
      text: form.text,
      attachments,
      groupId,
    });

    const actions: any[] = [
      toastActions.hideToast(),
      postsActions.updateFormText({
        formId: `comment_${originalParent}`,
        text: '',
      }),
      rouletteActions.clear(),
      postsActions.setPost({
        id: data.post.id,
        updated: data.post,
      }),
      attachActions.clear(`comment_${originalParent}`),
      postsActions.replyComment({ parentId: originalParent, comment: null }),
    ];

    if (form.replyTo && form.replyTo.level > 1) {
      actions.push(
        postsActions.pushToList({
          listId: `comments_${parentId}`,
          items: [data.post.id],
        }),
      );
    } else {
      actions.push(
        postsActions.unshiftToList({
          listId: `comments_${parentId}`,
          items: [data.post.id],
        }),
      );
      actions.push(postsActions.updateOrderList(String(`comments_${parentId}`)));
    }

    if (data.parent) {
      actions.push(
        postsActions.setPost({
          id: data.parent.id,
          updated: {
            ...data.parent,
            comments: data.parent.comments + 1,
          },
        }),
      );
    }

    yield put(batchActions(...actions));
    yield call(statReachGoal, 'comment');
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* deletePostWorker(action: DeletePostAction) {
  const postId = action.payload;
  const postsScheduled = yield select(postsScheduledSelector);
  const post = yield select((state: RootState) => postSelector(state, postId));
  const isScheduled = postsScheduled.includes(postId);
  const isForSponsors = post.type === PostType.PRIVATE_SUBSCRIPTION;

  try {
    yield put(
      postsActions.setPost({
        id: postId,
        updated: { isDeleted: true },
      }),
    );
    if (isScheduled) {
      if (post.ownerId < 0) {
        yield put(
          groupsActions.incrementScheduledCount({ groupId: -post.ownerId, value: -1 }),
        );
      } else {
        yield put(
          profileActions.incrementScheduledCount({ userId: post.ownerId, value: -1 }),
        );
      }
    }
    yield put(profileActions.decrementPostsCount(post.ownerId));

    if (isForSponsors) {
      yield put(profileActions.decrementSponsoredPostsCount(post.ownerId));
    }

    yield call(Api.post, `/posts/${postId}/delete`);
  } catch (e) {
    if (isScheduled) {
      if (post.ownerId < 0) {
        yield put(groupsActions.incrementScheduledCount({ groupId: -post.ownerId }));
      } else {
        yield put(profileActions.incrementScheduledCount({ userId: post.ownerId }));
      }
    }
    yield put(profileActions.incrementPostsCount(post.ownerId));

    if (isForSponsors) {
      yield put(profileActions.incrementSponsoredPostsCount(post.ownerId));
    }

    yield put(
      postsActions.setPost({
        id: postId,
        updated: { isDeleted: false },
      }),
    );
    yield put(toastActions.setToastFail(e.message));
  }
}

function* reportPostWorker(action: ReportPostAction) {
  const { postId, reason } = action.payload;
  try {
    yield call(Api.post, `/report`, {
      reason,
      objectId: postId,
      objectType: 'post',
    });
    yield put(feedActions.hidePostsFromUI(postId));
    yield put(toastActions.setToastSuccess(t('post_report_sent')));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* reportCommentWorker(action: ReportCommentAction) {
  const { commentId, reason } = action.payload;
  try {
    yield call(Api.post, `/report`, {
      reason,
      objectId: commentId,
      objectType: 'comment',
    });
    yield put(toastActions.setToastSuccess(t('comment_report_sent')));
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* restorePostWorker(action: DeletePostAction) {
  const postId = action.payload;
  const post = yield select((state: RootState) => postSelector(state, postId));
  const isForSponsors = post.type === PostType.PRIVATE_SUBSCRIPTION;

  try {
    yield put(
      postsActions.setPost({
        id: postId,
        updated: { isDeleted: false },
      }),
    );
    yield put(profileActions.incrementPostsCount(post.ownerId));

    if (isForSponsors) {
      yield put(profileActions.incrementSponsoredPostsCount(post.ownerId));
    }

    yield call(Api.post, `/posts/${postId}/restore`);
  } catch (e) {
    yield put(
      postsActions.setPost({
        id: postId,
        updated: { isDeleted: true },
      }),
    );

    yield put(profileActions.decrementPostsCount(post.ownerId));

    if (isForSponsors) {
      yield put(profileActions.decrementSponsoredPostsCount(post.ownerId));
    }

    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadEditPostWorker(action: LoadEditDataAction) {
  try {
    const postId = action.payload;
    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, `edit_post_${postId}`),
    );

    if (
      (Array.isArray(attachments) && attachments.length) ||
      attachments === 'photo_uploading'
    ) {
      return;
    }

    let post = yield select((state: RootState) => postSelector(state, postId));
    if (!post) {
      const data = yield call(Api.get, `/posts/${postId}`);
      post = data.post;
    }

    yield put(
      postsActions.updateFormText({
        formId: `edit_post_${postId}`,
        text: post.text,
      }),
    );

    yield put(rouletteActions.clear());
    yield put(
      attachActions.initFromAttachments({
        pickerId: `edit_post_${postId}`,
        attachments: post.attachments,
      }),
    );

    if (post.createdAt * 1000 > Date.now()) {
      yield put(
        attachActions.setTimer({
          pickerId: `edit_post_${postId}`,
          time: post.createdAt * 1000,
        }),
      );
    }
  } catch (e) {}
}

function* savePostWorker(action: SavePostAction) {
  try {
    const postId = action.payload;
    const form = yield select((state: RootState) =>
      createPostFormSelector(state, `edit_post_${postId}`),
    );
    const attachments = yield select((state: RootState) =>
      attachItemsSelector(state, `edit_post_${postId}`),
    );
    const { timers } = yield select(attachSelector);
    const timer = timers[`edit_post_${postId}`];
    if (attachments === 'photo_uploading') {
      return yield put(toastActions.setToastFail(t('photo_uploading_err')));
    }

    if (form.text.length === 0 && !attachments.length) {
      return yield put(toastActions.setToastFail(t('im_empty_error')));
    }

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, `/posts/${postId}/edit`, {
      text: form.text,
      attachments,
      timer: timer > 0 ? Math.floor(timer / 1000) : 0,
    });
    yield put(toastActions.setToastSuccess());
    yield put(
      postsActions.updateFormText({
        formId: `edit_post_${postId}`,
        text: '',
      }),
    );
    yield put(rouletteActions.clear());

    if (data.post) {
      yield put(
        postsActions.setPost({
          id: data.post.id,
          updated: data.post,
        }),
      );
    }
    yield put(attachActions.clear(`edit_post_${postId}`));

    if (data.moderation) {
      yield call(hideModal);
      yield call(confirmSheet, t('post_edit_moderation'));
    }

    history.back();
  } catch (e) {
    console.log('e', e);
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadMoreCommentsWorker(action: LoadMoreCommentsAction) {
  try {
    const postId = action.payload;
    const { commentsNextFrom } = yield select(postsSelector);
    const data = yield call(Api.post, `/posts/${postId}/comments`, {
      startFrom: +commentsNextFrom[postId],
      sort: 'popular',
    });
    yield put(mainActions.setUsers(data.users));
    yield put(postsActions.setPosts(data.comments));
    yield put(
      postsActions.pushToList({
        listId: `comments_${postId}`,
        items: data.comments.map((comment) => comment.id),
      }),
    );
    yield put(
      postsActions.setCommentsNextFrom({
        postId,
        nextFrom: data.nextFrom,
      }),
    );
  } catch (e) {}
}

function* shareWorker(action: ShareAction) {
  try {
    const link = action.payload;
    const { currentUserId } = yield select(authSelector);

    yield call(showModal, 'post');
    yield put(
      batchActions(
        attachActions.clear(`post_${currentUserId}`),
        attachActions.setLink({
          pickerId: `post_${currentUserId}`,
          link,
        }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadRepliesWorker(action: LoadRepliesAction) {
  try {
    const { postId } = action.payload;
    const { replies } = yield select(postsSelector);
    const repliesState = replies[postId] || {};

    const data = yield call(Api.post, `/posts/${postId}/comments`, {
      startFrom: +repliesState.nextFrom,
      sort: 'recent',
      limit: 5,
    });

    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        postsActions.setPosts(data.comments),
        postsActions.pushToList({
          listId: `comments_${postId}`,
          items: data.comments.map((comment) => comment.id),
        }),
        postsActions.setRepliesLoaded({
          postId,
          nextFrom: data.nextFrom,
        }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadScheduledWorker(action: LoadScheduledAction) {
  try {
    const { ownerId } = action.payload;

    const data = yield call(Api.get, `/posts/${ownerId}/scheduled`);
    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        mainActions.setGroups(data.groups),
        postsActions.setPosts(data.posts),
        postsActions.setScheduled(data.scheduled),
      ),
    );
  } catch (e) {
    yield put(postsActions.scheduledFailed());
  }
}

function* publishScheduledWorker(action: PublishScheduledAction) {
  try {
    const postId = action.payload;
    yield put(toastActions.setToastLoading());

    yield call(Api.post, `/posts/scheduled/${postId}/publish`);

    const { scheduled } = yield select(postsSelector);
    const scheduledCopy = [...scheduled];
    for (let i = 0; i < scheduled.length; i++) {
      if (scheduledCopy[i] === postId) {
        const post = yield select((state: RootState) => postSelector(state, postId));

        yield put(
          batchActions(
            postsActions.setPost({
              id: postId,
              updated: {
                createdAt: Date.now() / 1000,
              },
            }),
            postsActions.unshiftToList({
              listId: post.ownerId,
              items: [postId],
            }),
            postsActions.updateOrderList(String(post.ownerId)),
          ),
        );
        scheduledCopy.splice(i, 1);
        break;
      }
    }

    const post = yield select((state: RootState) => postSelector(state, postId));
    if (post.ownerId < 0) {
      yield put(
        groupsActions.incrementScheduledCount({ groupId: -post.ownerId, value: -1 }),
      );
    } else {
      yield put(
        profileActions.incrementScheduledCount({ userId: post.ownerId, value: -1 }),
      );
    }

    yield put(postsActions.setScheduled(scheduledCopy));
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* viewPostWorker() {
  const { viewQueue } = yield select(postsSelector);

  if (viewQueue.length >= 100) {
    yield call(sendSeenPostsWorker);
  } else {
    yield delay(2000);
    yield call(sendSeenPostsWorker);
  }
}

function* sendSeenPostsWorker() {
  const { isLogged } = yield select(authSelector);
  if (!isLogged) {
    return;
  }

  const { viewQueue } = yield select(postsSelector);

  if (Object.keys(viewQueue).length === 0) {
    return;
  }
  const views = Object.values(viewQueue) as ViewPost[];

  try {
    yield put(postsActions.clearViewPostsList());
    yield call(Api.post, '/posts/view', {
      views: views.filter(
        (view) => view.postViewDuration && view.postViewDuration < 15000,
      ),
    });
  } catch (e) {
    yield put(postsActions.viewMultiPost(views));
  }
}

function* pinPostWorker(action: PinPostAction) {
  const postId = action.payload;
  const post = yield select(postSelector, postId);

  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/posts/${postId}/pin`);
    if (post) {
      const listId = post.parentId ? `comments_${post.parentId}` : String(post.ownerId);
      yield put(
        batchActions(
          postsActions.setPost({
            id: postId,
            updated: {
              pinned: true,
            },
          }),
          postsActions.updateOrderList(listId),
        ),
      );
    }
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    if (e.code === 'limit') {
      yield put(
        toastActions.setToastFail(
          t(
            post?.parentId ? 'comment_pin_error_limit' : 'post_action_pin_error_limit',
            e.params,
          ),
        ),
      );
    } else {
      yield put(toastActions.setToastFail(e.message));
    }
  }
}

function* unpinPostWorker(action: PinPostAction) {
  try {
    const postId = action.payload;
    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/posts/${postId}/unpin`);

    const post = yield select(postSelector, postId);
    if (post) {
      yield put(
        batchActions(
          postsActions.setPost({
            id: postId,
            updated: {
              pinned: false,
            },
          }),
          postsActions.updateOrderList(
            post.parentId ? `comments_${post.parentId}` : String(post.ownerId),
          ),
        ),
      );
    }

    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* hideAuthorWorker(action: HideAuthorAction) {
  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/feed/hide_source', {
      ownerId: action.payload,
    });
    yield put(toastActions.setToastSuccess());

    const { posts } = yield select(postsSelector);
    const postsId: number[] = [];
    for (let postId in posts) {
      if (+posts[postId].ownerId === +action.payload) {
        postsId.push(+postId);
      }
    }

    if (postsId.length > 0) {
      yield put(feedActions.hidePostsFromUI(postsId));
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* sendAdConversionWorker(action: SendAdConversionAction) {
  try {
    yield call(Api.post, `/ads/conversion?aid=${action.payload.adId}`);
  } catch (e) {}
}

export function* postsSaga() {
  yield all([
    takeLatest(postsActions.publish, publishWorker),
    takeLatest(postsActions.toggleLike, toggleLikeWorker),
    takeEvery(postsActions.loadPost, loadPostWorker),
    takeLatest(postsActions.publishComment, publishCommentWorker),
    takeLatest(postsActions.deletePost, deletePostWorker),
    takeLatest(postsActions.reportPost, reportPostWorker),
    takeLatest(postsActions.reportComment, reportCommentWorker),
    takeLatest(postsActions.restorePost, restorePostWorker),
    takeLatest(postsActions.loadEditPost, loadEditPostWorker),
    takeLatest(postsActions.savePost, savePostWorker),
    takeLatest(postsActions.loadMoreComments, loadMoreCommentsWorker),
    takeLatest(postsActions.share, shareWorker),
    takeEvery(postsActions.loadReplies, loadRepliesWorker),
    takeLatest(postsActions.loadScheduled, loadScheduledWorker),
    takeLatest(postsActions.publishScheduled, publishScheduledWorker),
    takeLatest(postsActions.viewPost, viewPostWorker),
    takeLatest(postsActions.sendSeenPosts, sendSeenPostsWorker),
    takeLatest(postsActions.pinPost, pinPostWorker),
    takeLatest(postsActions.unpinPost, unpinPostWorker),
    takeLatest(postsActions.hideAuthor, hideAuthorWorker),
    takeLatest(postsActions.sendAdConversion, sendAdConversionWorker),
  ]);
}
